import { useState, useRef, useEffect } from "react";
import clsx from "clsx";

import { KEYS, EVENT } from "@excalidraw/common";

import { eyeIcon } from "../icons";
import { InlineIcon } from "../InlineIcon";
import { t } from "../../i18n";

import "./DragInput.scss";

interface OpacityProps {
  updateData: (value: number) => void;
  app: any; // AppClassProperties
}

const Opacity = ({ updateData, app }: OpacityProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  // Calculate current opacity value (same logic as Range component)
  const selectedElements = app.scene.getSelectedElements(app.state);
  const firstElement = selectedElements.at(0);
  const leastCommonOpacity = selectedElements.reduce(
    (acc: number | null, element: any) => {
      if (acc != null && acc !== element.opacity) {
      }
      if (acc == null || acc > element.opacity) {
        return element.opacity;
      }
      return acc;
    },
    firstElement?.opacity ?? null,
  );

  const value = leastCommonOpacity ?? app.state.currentItemOpacity;
  const [inputValue, setInputValue] = useState(`${value.toString()}%`);

  // Update input value when the actual value changes
  useEffect(() => {
    setInputValue(`${value.toString()}%`);
  }, [value]);

  const handleInputBlur = () => {
    // Remove % symbol if present for parsing
    const cleanValue = inputValue.replace("%", "");
    const parsed = parseFloat(cleanValue);
    if (isNaN(parsed)) {
      setInputValue(`${value.toString()}%`);
      return;
    }

    const clampedValue = Math.max(0, Math.min(100, parsed));

    // Use updateData to trigger the action (same as Range component)
    updateData(clampedValue);

    setInputValue(`${clampedValue.toString()}%`);
  };

  const handleDragChange = (newValue: number) => {
    const clampedValue = Math.max(0, Math.min(100, Math.round(newValue)));
    updateData(clampedValue);
    setInputValue(`${clampedValue.toString()}%`);
  };

  return (
    <div
      className={clsx("drag-input-container")}
      data-testid="opacity"
      title={t("stats.tooltips.opacity")}
    >
      <div
        className="drag-input-label"
        ref={labelRef}
        onPointerDown={(event) => {
          if (inputRef.current) {
            document.body.classList.add("excalidraw-cursor-resize");

            const startValue = parseFloat(inputValue.replace("%", "")) || 0;
            let lastPointer: { x: number; y: number } | null = null;
            let accumulatedChange = 0;

            const onPointerMove = (event: PointerEvent) => {
              if (lastPointer) {
                const instantChange = event.clientX - lastPointer.x;

                if (instantChange !== 0) {
                  // Make drag more sensitive by dividing by 2 (2px movement = 1% change)
                  accumulatedChange += instantChange / 2;
                  const newValue = startValue + accumulatedChange;
                  handleDragChange(newValue);
                }
              }

              lastPointer = {
                x: event.clientX,
                y: event.clientY,
              };
            };

            const onPointerUp = () => {
              window.removeEventListener(
                EVENT.POINTER_MOVE,
                onPointerMove,
                false,
              );
              window.removeEventListener(EVENT.POINTER_UP, onPointerUp, false);
              document.body.classList.remove("excalidraw-cursor-resize");
              lastPointer = null;
              accumulatedChange = 0;
            };

            window.addEventListener(EVENT.POINTER_MOVE, onPointerMove, false);
            window.addEventListener(EVENT.POINTER_UP, onPointerUp, false);
          }
        }}
        onPointerEnter={() => {
          if (labelRef.current) {
            labelRef.current.style.cursor = "ew-resize";
          }
        }}
      >
        <InlineIcon icon={eyeIcon} />
      </div>
      <input
        className="drag-input"
        autoComplete="off"
        spellCheck="false"
        onKeyDown={(event) => {
          const eventTarget = event.target;
          if (eventTarget instanceof HTMLInputElement) {
            if (event.key === KEYS.ENTER) {
              handleInputBlur();
              eventTarget.blur();
            } else if (
              event.key === KEYS.ARROW_UP ||
              event.key === KEYS.ARROW_DOWN
            ) {
              event.preventDefault();

              const currentValue =
                parseFloat(eventTarget.value.replace("%", "")) || 0;
              let nextValue: number;

              if (event.shiftKey) {
                // Shift + arrow: snap to base unit of 4% (4%, 8%, 12%, 16%...)
                if (event.key === KEYS.ARROW_UP) {
                  nextValue = Math.ceil(currentValue / 4) * 4;
                  // If already on a multiple of 4, go to next multiple
                  if (nextValue === currentValue) {
                    nextValue = currentValue + 4;
                  }
                } else {
                  nextValue = Math.floor(currentValue / 4) * 4;
                  // If already on a multiple of 4, go to previous multiple
                  if (nextValue === currentValue) {
                    nextValue = currentValue - 4;
                  }
                }
              } else if (event.key === KEYS.ARROW_UP) {
                // Regular arrow: round to closest whole percentage
                nextValue = Math.ceil(currentValue);
                // If already whole number, increment by 1
                if (nextValue === currentValue) {
                  nextValue = currentValue + 1;
                }
              } else {
                nextValue = Math.floor(currentValue);
                // If already whole number, decrement by 1
                if (nextValue === currentValue) {
                  nextValue = currentValue - 1;
                }
              }

              // Clamp to 0-100% range
              nextValue = Math.max(0, Math.min(100, nextValue));

              // Update the value
              handleDragChange(nextValue);
            }
          }
        }}
        ref={inputRef}
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
        }}
        onFocus={(event) => {
          event.target.select();
        }}
        onBlur={handleInputBlur}
      />
    </div>
  );
};

export default Opacity;
